import { useState, useEffect, useRef } from 'react';
import { fetchAvatars, submitVote } from '../utils/api';
import { getSession, spendTokens } from '../utils/session';
import { calculateAuraStats, getAuraBadge } from '../utils/aura';

const GOOD_CATEGORIES = [
  { id: 'drip_king', name: 'Drip King', emoji: '👑', icon: '/dripkingicon.png', type: 'good' },
  { id: 'drip_queen', name: 'Drip Queen', emoji: '👸', icon: '/dripqueenicon.png', type: 'good' },
  { id: 'bussin', name: "Bussin'", emoji: '✨', icon: '/bussinicon.png', type: 'good' },
  { id: 'fire_fit', name: 'Fire Fit', emoji: '🔥', icon: '/firefiticon.png', type: 'good' },
];

const BAD_CATEGORIES = [
  { id: 'mid', name: 'Mid', emoji: '😐', icon: '/midicon.png', type: 'bad' },
  { id: 'ohio', name: 'Ohio', emoji: '🌽', icon: '/ohioicon.png', type: 'bad' },
  { id: 'cringe', name: 'Cringe', emoji: '😬', icon: '/cringeicon.png', type: 'bad' },
  { id: 'cooked', name: 'Cooked', emoji: '💀', icon: '/cookedicon.png', type: 'bad' },
];

const CATEGORIES = [...GOOD_CATEGORIES, ...BAD_CATEGORIES];

function VotingGallery({ session, onVoteComplete, onNavigate }) {
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0].id);
  const [votingOn, setVotingOn] = useState(null);
  const [error, setError] = useState('');
  const [rateLimitMessage, setRateLimitMessage] = useState('');
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const [currentTokens, setCurrentTokens] = useState(session?.tokens || 0);
  const rateLimitRef = useRef(null);

  // Update local token state when session prop changes
  useEffect(() => {
    setCurrentTokens(session?.tokens || 0);
  }, [session]);

  useEffect(() => {
    loadAvatars();
  }, []);

  // Countdown timer for rate limit
  useEffect(() => {
    if (rateLimitCountdown > 0) {
      const timer = setTimeout(() => {
        setRateLimitCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (rateLimitCountdown === 0 && rateLimitMessage) {
      setRateLimitMessage('');
    }
  }, [rateLimitCountdown, rateLimitMessage]);

  // Scroll to rate limit message when it appears
  useEffect(() => {
    if (rateLimitMessage && rateLimitRef.current) {
      rateLimitRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [rateLimitMessage]);

  const loadAvatars = async () => {
    setLoading(true);
    try {
      const data = await fetchAvatars();
      setAvatars(data);
    } catch (err) {
      setError('Failed to load avatars');
      console.error('Load avatars error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (avatarId, isPowerVote = false) => {
    const sessionData = getSession();
    const cost = isPowerVote ? 12 : 5;
    const voteMultiplier = isPowerVote ? 3 : 1;

    // Check if user has enough Lit-Bits
    if (!sessionData || sessionData.tokens < cost) {
      // Redirect to buy page instead of showing alert
      if (onNavigate) {
        onNavigate('buy');
      }
      return;
    }

    setVotingOn(avatarId);

    try {
      console.log('🗳️ Submitting vote - backend will deduct', cost, 'Lit-Bits');

      // Submit vote - backend handles token deduction
      const result = await submitVote(avatarId, selectedCategory, true, voteMultiplier);

      if (result.success) {
        console.log('✅ Vote successful - balance:', result.balance);

        // Update local avatar data (apply multiplier to UI)
        setAvatars(prev => prev.map(avatar => {
          if (avatar.id === avatarId) {
            return {
              ...avatar,
              votes: {
                ...avatar.votes,
                [selectedCategory]: (avatar.votes[selectedCategory] || 0) + voteMultiplier
              }
            };
          }
          return avatar;
        }));

        // Update balance immediately from vote response
        if (result.balance !== undefined) {
          const sessionData = getSession();
          if (sessionData) {
            sessionData.tokens = result.balance;
            localStorage.setItem('dripbattle_session', JSON.stringify(sessionData));
          }
          setCurrentTokens(result.balance);
          console.log('💰 Balance updated to:', result.balance);

          // Immediately update parent component's live balance
          await onVoteComplete(result.balance);
        } else {
          // Fallback if no balance in response
          await onVoteComplete();
        }
      } else {
        console.error('❌ Vote failed:', result);

        // Check if it's a rate limit error
        if (result.status === 429) {
          setRateLimitMessage('🚨 Whoa there! You\'re voting too fast! The limit is 100 votes per minute. Take a breather!');
          setRateLimitCountdown(60);
        } else {
          alert('Failed to submit vote. Please try again.');
        }
      }
    } catch (err) {
      console.error('💥 Vote error:', err);

      // Check if it's a rate limit error
      if (err.response && err.response.status === 429) {
        setRateLimitMessage('🚨 Whoa there! You\'re voting too fast! The limit is 100 votes per minute. Take a breather!');
        setRateLimitCountdown(60);
      } else {
        alert('An error occurred while voting.');
      }
    } finally {
      setVotingOn(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner large"></div>
        <p>Loading avatars...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-text">{error}</p>
        <button onClick={loadAvatars} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="voting-container">
      <div className="voting-header">
        <h1 className="voting-title">Vote on Avatars</h1>
        <p className="votes-remaining">
          Lit-Bits Remaining: <span className="vote-count">{currentTokens}</span>
        </p>
        <p className="vote-info">💰 5 Lit-Bits per vote • 12 Lit-Bits for Power Vote (3x impact)</p>
      </div>

      {/* Rate Limit Message */}
      {rateLimitMessage && (
        <div className="rate-limit-banner" ref={rateLimitRef}>
          <p className="rate-limit-message">{rateLimitMessage}</p>
          <p className="rate-limit-timer">
            ⏱️ You can vote again in: <strong>{rateLimitCountdown}s</strong>
          </p>
        </div>
      )}

      {/* Category Selector */}
      <div className="category-selector">
        <h2 className="selector-title">Select Category:</h2>

        <div className="category-section">
          <h3 className="category-section-title good">🔥 Good Vibes</h3>
          <div className="category-buttons">
            {GOOD_CATEGORIES.map(category => (
              <button
                key={category.id}
                className={`category-button good ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.icon ? (
                  <img src={category.icon} alt={category.name} className="category-icon" />
                ) : (
                  category.emoji
                )} {category.name}
              </button>
            ))}
          </div>
        </div>

        <div className="category-section">
          <h3 className="category-section-title bad">💀 L Energy</h3>
          <div className="category-buttons">
            {BAD_CATEGORIES.map(category => (
              <button
                key={category.id}
                className={`category-button bad ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.icon ? (
                  <img src={category.icon} alt={category.name} className="category-icon" />
                ) : (
                  category.emoji
                )} {category.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Avatar Gallery */}
      <div className="avatar-gallery">
        {avatars.length === 0 ? (
          <div className="empty-state">
            <p>No avatars to vote on yet. Be the first to submit!</p>
          </div>
        ) : (
          avatars.map(avatar => {
            // Calculate Aura stats for badge display
            const auraStats = calculateAuraStats(
              avatar.auraStats?.fireSwipes || 0,
              avatar.auraStats?.leftSwipes || 0
            );
            const auraBadge = getAuraBadge(auraStats.auraPercent, auraStats.totalSwipes);

            return (
              <div key={avatar.id} className="avatar-card">
                <div
                  className="avatar-image-container"
                  onClick={() => onNavigate && onNavigate('profile', avatar.username)}
                  style={{ cursor: 'pointer' }}
                >
                  <img
                    src={avatar.avatarUrl}
                    alt={`${avatar.username}'s avatar`}
                    className="avatar-image"
                  />

                  {/* Aura Badge Overlay */}
                  <div className="aura-badge-overlay">
                    <img src={auraBadge.emoji} alt={auraBadge.label} className="aura-badge-image" />
                  </div>
                </div>

                <div className="avatar-info">
                  <h3
                    className="avatar-username"
                    onClick={() => onNavigate && onNavigate('profile', avatar.username)}
                    style={{ cursor: 'pointer' }}
                  >
                    {avatar.username}
                    {avatar.isVerified && <span className="verified-badge" title="Verified Roblox Account">✓</span>}
                  </h3>
                  <p className="avatar-votes">
                    {avatar.votes[selectedCategory] || 0} votes in {CATEGORIES.find(c => c.id === selectedCategory)?.emoji}
                  </p>
                  {auraStats.totalSwipes > 0 && (
                    <p className="avatar-aura">
                      {auraStats.auraPercent}% Aura ({auraStats.totalSwipes} swipes)
                    </p>
                  )}
                </div>

                <div className="vote-buttons">
                  <button
                    className="vote-button single-vote"
                    onClick={() => handleVote(avatar.id, false)}
                    disabled={votingOn === avatar.id}
                  >
                    {votingOn === avatar.id ? (
                      <span>
                        <span className="spinner small"></span> Voting...
                      </span>
                    ) : (
                      <>
                        🔥 Vote (5 LB)
                      </>
                    )}
                  </button>
                  <button
                    className="vote-button power-vote-button"
                    onClick={() => handleVote(avatar.id, true)}
                    disabled={votingOn === avatar.id}
                  >
                    {votingOn === avatar.id ? (
                      <span>
                        <span className="spinner small"></span> Voting...
                      </span>
                    ) : (
                      <>
                        💪 Power Vote (12 LB)
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default VotingGallery;
